<template>
  <div id="raft-js-app-component">
    <template v-if="message">
      <div class="messages status messages--status">
        {{ message }}
      </div>
    </template>
    <template v-if="hasErrors">
      <div
        v-for="(error, index) in errors"
        :key="'error-' + index"
        class="messages error messages--error"
      >
        {{ error }}
      </div>
      <ErrorStateLinks />
    </template>
    <router-view
      v-if="hasRoute"
      @form-error-message="addError"
      @form-message="setMessage"
    >
    </router-view>
    <slot v-if="hasSlot"></slot>
  </div>
</template>

<script>
export default {
  name: "App",
  components: {
    ErrorStateLinks: () => import("./raft/ErrorStateLinks.vue"),
  },
  data() {
    return {
      /**
       * @type {string[]}
       * @description An array to store error messages.
       */
      errors: [],
      /**
       * @type {string|null}
       * @description The message to be displayed.
       */
      message: null,
    };
  },
  computed: {
    hasErrors() {
      return this.errors.length > 0;
    },
    hasRoute() {
      return this.$route;
    },
    hasSlot() {
      return (
        !!this.$slots.default &&
        !!this.$slots.default[0] &&
        this.$slots.default[0].text !== "<>"
      );
    },
  },
  methods: {
    /**
     * Adds an error message to the error list.
     *
     * @typedef {Object} ErrorPayload
     * @prop {string} message - The error message.
     * @prop {any} data - Additional data related to the error.
     *
     * @param {string|ErrorPayload} error - The error message to be added.
     */
    addError(error) {
      if (typeof error === "object") {
        let newMessage = error.message;
        // Unpack details from components in the former chemdb_type bundle.
        for (const item of error.data?.errors ?? []) {
          if (item.hasOwnProperty("detail")) {
            newMessage += ` ${item.detail}`;
          }
        }
        this.errors.push(newMessage);
        // eslint-disable-next-line no-console
        console.error(error.data);
        return;
      }
      this.errors.push(error);
    },
    /**
     * Replaces the current message, if any.
     *
     * @param {string} message - The message to be set.
     */
    setMessage(message) {
      this.message = message;
    },
  },
};
</script>

<style>
:root {
  --drop-shadow-gray: 0px 1px 2px rgba(0, 0, 0, 0.5);
  --drop-shadow-gray-50: 0px 1px 2px rgba(0, 0, 0, 0.125);
  /* Font styles */
  --font-system-ui:
    system-ui, "Segoe UI", Roboto, Helvetica, Arial, sans-serif,
    "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol";
  --font-size-base: 16px;
  --font-size-50: 0.79rem;
  --font-size-75: 0.889rem;
  --font-size-100: 1rem;
  --font-size-200: 1.125rem;
  --font-size-300: 1.266rem;
  --font-size-400: 1.424rem;
  --font-size-500: 1.602rem;
  --font-size-600: 1.802rem;
  --font-size-700: 2.027rem;
  --font-size-800: 2.281rem;
  --font-size-900: 2.566rem;
  --font-size-1000: 2.887rem;
  --font-size-1100: 3.247rem;
  --font-size-1200: 3.653rem;
  --font-size-1300: 4.11rem;
  /* Color styles */
  --gray-50: rgba(255, 255, 255, 1);
  --gray-75: rgba(253, 253, 253, 1);
  --gray-100: rgba(248, 248, 248, 1);
  --gray-200: rgba(230, 230, 230, 1);
  --gray-300: rgba(213, 213, 213, 1);
  --gray-400: rgba(177, 177, 177, 1);
  --gray-500: rgba(144, 144, 144, 1);
  --gray-600: rgba(109, 109, 109, 1);
  --gray-700: rgba(70, 70, 70, 1);
  --gray-800: rgba(34, 34, 34, 1);
  --gray-900: rgba(0, 0, 0, 1);
  --red-100: rgba(252, 236, 232, 1);
  --red-200: rgba(250, 222, 215, 1);
  --red-300: rgba(247, 207, 197, 1);
  --red-400: rgba(244, 186, 172, 1);
  --red-500: rgba(241, 160, 141, 1);
  --red-600: rgba(239, 131, 108, 1);
  --red-700: rgba(229, 102, 80, 1);
  --red-800: rgba(216, 72, 54, 1);
  --red-900: rgba(194, 47, 34, 1);
  --red-1000: rgba(165, 33, 22, 1);
  --red-1100: rgba(134, 25, 16, 1);
  --red-1200: rgba(106, 17, 10, 1);
  --red-1300: rgba(81, 11, 6, 1);
  --red-1400: rgba(61, 6, 3, 1);
  --orange-100: rgba(252, 237, 208, 1);
  --orange-200: rgba(250, 224, 179, 1);
  --orange-300: rgba(246, 212, 154, 1);
  --orange-400: rgba(245, 190, 113, 1);
  --orange-500: rgba(242, 164, 79, 1);
  --orange-600: rgba(231, 139, 55, 1);
  --orange-700: rgba(213, 117, 44, 1);
  --orange-800: rgba(189, 99, 37, 1);
  --orange-900: rgba(165, 82, 30, 1);
  --orange-1000: rgba(138, 66, 23, 1);
  --orange-1100: rgba(113, 52, 16, 1);
  --orange-1200: rgba(90, 39, 11, 1);
  --orange-1300: rgba(67, 28, 7, 1);
  --orange-1400: rgba(49, 20, 4, 1);
  --yellow-100: rgba(249, 241, 163, 1);
  --yellow-200: rgba(245, 231, 107, 1);
  --yellow-300: rgba(243, 218, 73, 1);
  --yellow-400: rgba(227, 199, 66, 1);
  --yellow-500: rgba(209, 180, 59, 1);
  --yellow-600: rgba(190, 160, 52, 1);
  --yellow-700: rgba(170, 141, 45, 1);
  --yellow-800: rgba(150, 121, 38, 1);
  --yellow-900: rgba(128, 103, 31, 1);
  --yellow-1000: rgba(108, 84, 24, 1);
  --yellow-1100: rgba(87, 68, 18, 1);
  --yellow-1200: rgba(69, 52, 11, 1);
  --yellow-1300: rgba(51, 38, 6, 1);
  --yellow-1400: rgba(38, 27, 4, 1);
  --chartreuse-100: rgba(225, 251, 130, 1);
  --chartreuse-200: rgba(211, 243, 100, 1);
  --chartreuse-300: rgba(197, 232, 84, 1);
  --chartreuse-400: rgba(179, 215, 72, 1);
  --chartreuse-500: rgba(161, 196, 62, 1);
  --chartreuse-600: rgba(144, 176, 54, 1);
  --chartreuse-700: rgba(126, 155, 46, 1);
  --chartreuse-800: rgba(110, 135, 39, 1);
  --chartreuse-900: rgba(93, 115, 32, 1);
  --chartreuse-1000: rgba(77, 95, 25, 1);
  --chartreuse-1100: rgba(62, 76, 18, 1);
  --chartreuse-1200: rgba(47, 59, 12, 1);
  --chartreuse-1300: rgba(35, 44, 7, 1);
  --chartreuse-1400: rgba(25, 31, 4, 1);
  --celery-100: rgba(214, 251, 196, 1);
  --celery-200: rgba(190, 244, 166, 1);
  --celery-300: rgba(170, 236, 144, 1);
  --celery-400: rgba(142, 221, 120, 1);
  --celery-500: rgba(116, 204, 97, 1);
  --celery-600: rgba(91, 184, 75, 1);
  --celery-700: rgba(75, 164, 58, 1);
  --celery-800: rgba(63, 143, 45, 1);
  --celery-900: rgba(53, 122, 38, 1);
  --celery-1000: rgba(43, 101, 32, 1);
  --celery-1100: rgba(33, 82, 25, 1);
  --celery-1200: rgba(24, 63, 19, 1);
  --celery-1300: rgba(16, 47, 13, 1);
  --celery-1400: rgba(9, 33, 8, 1);
  --green-100: rgba(214, 247, 226, 1);
  --green-200: rgba(188, 242, 209, 1);
  --green-300: rgba(161, 234, 191, 1);
  --green-400: rgba(135, 219, 172, 1);
  --green-500: rgba(112, 201, 151, 1);
  --green-600: rgba(93, 181, 132, 1);
  --green-700: rgba(76, 161, 114, 1);
  --green-800: rgba(62, 141, 97, 1);
  --green-900: rgba(52, 120, 81, 1);
  --green-1000: rgba(42, 99, 65, 1);
  --green-1100: rgba(32, 80, 53, 1);
  --green-1200: rgba(26, 62, 41, 1);
  --green-1300: rgba(21, 45, 30, 1);
  --green-1400: rgba(16, 31, 22, 1);
  --blue-100: rgba(227, 241, 254, 1);
  --blue-200: rgba(208, 231, 253, 1);
  --blue-300: rgba(189, 221, 252, 1);
  --blue-400: rgba(162, 205, 249, 1);
  --blue-500: rgba(135, 185, 245, 1);
  --blue-600: rgba(108, 165, 240, 1);
  --blue-700: rgba(81, 144, 236, 1);
  --blue-800: rgba(55, 120, 235, 1);
  --blue-900: rgba(41, 100, 212, 1);
  --blue-1000: rgba(33, 83, 176, 1);
  --blue-1100: rgba(25, 67, 140, 1);
  --blue-1200: rgba(18, 52, 109, 1);
  --blue-1300: rgba(11, 38, 81, 1);
  --blue-1400: rgba(7, 27, 58, 1);
  --magenta-100: rgba(252, 235, 241, 1);
  --magenta-200: rgba(249, 221, 232, 1);
  --magenta-300: rgba(247, 204, 220, 1);
  --magenta-400: rgba(244, 181, 205, 1);
  --magenta-500: rgba(241, 154, 188, 1);
  --magenta-600: rgba(234, 126, 169, 1);
  --magenta-700: rgba(222, 100, 151, 1);
  --magenta-800: rgba(205, 74, 129, 1);
  --magenta-900: rgba(184, 52, 104, 1);
  --magenta-1000: rgba(159, 35, 85, 1);
  --magenta-1100: rgba(130, 24, 69, 1);
  --magenta-1200: rgba(102, 16, 55, 1);
  --magenta-1300: rgba(76, 12, 41, 1);
  --magenta-1400: rgba(55, 11, 29, 1);
  --fuchsia-100: rgba(251, 234, 251, 1);
  --fuchsia-200: rgba(249, 219, 248, 1);
  --fuchsia-300: rgba(245, 201, 245, 1);
  --fuchsia-400: rgba(240, 177, 242, 1);
  --fuchsia-500: rgba(231, 151, 238, 1);
  --fuchsia-600: rgba(222, 123, 231, 1);
  --fuchsia-700: rgba(208, 95, 220, 1);
  --fuchsia-800: rgba(189, 70, 200, 1);
  --fuchsia-900: rgba(167, 50, 177, 1);
  --fuchsia-1000: rgba(144, 29, 153, 1);
  --fuchsia-1100: rgba(117, 21, 125, 1);
  --fuchsia-1200: rgba(91, 18, 97, 1);
  --fuchsia-1300: rgba(65, 19, 68, 1);
  --fuchsia-1400: rgba(46, 15, 47, 1);
  --purple-100: rgba(244, 235, 254, 1);
  --purple-200: rgba(235, 222, 253, 1);
  --purple-300: rgba(226, 209, 252, 1);
  --purple-400: rgba(214, 188, 249, 1);
  --purple-500: rgba(198, 166, 247, 1);
  --purple-600: rgba(181, 141, 245, 1);
  --purple-700: rgba(165, 117, 241, 1);
  --purple-800: rgba(147, 91, 236, 1);
  --purple-900: rgba(127, 66, 223, 1);
  --purple-1000: rgba(106, 44, 203, 1);
  --purple-1100: rgba(85, 26, 176, 1);
  --purple-1200: rgba(64, 18, 142, 1);
  --purple-1300: rgba(47, 18, 102, 1);
  --purple-1400: rgba(32, 16, 70, 1);
  --indigo-100: rgba(237, 238, 254, 1);
  --indigo-200: rgba(224, 226, 253, 1);
  --indigo-300: rgba(211, 213, 252, 1);
  --indigo-400: rgba(193, 196, 250, 1);
  --indigo-500: rgba(172, 175, 249, 1);
  --indigo-600: rgba(149, 153, 248, 1);
  --indigo-700: rgba(127, 132, 244, 1);
  --indigo-800: rgba(104, 109, 236, 1);
  --indigo-900: rgba(83, 88, 220, 1);
  --indigo-1000: rgba(65, 70, 195, 1);
  --indigo-1100: rgba(50, 54, 162, 1);
  --indigo-1200: rgba(38, 41, 129, 1);
  --indigo-1300: rgba(27, 30, 96, 1);
  --indigo-1400: rgba(20, 22, 69, 1);
  --cyan-100: rgba(207, 247, 254, 1);
  --cyan-200: rgba(180, 238, 253, 1);
  --cyan-300: rgba(158, 229, 247, 1);
  --cyan-400: rgba(128, 213, 240, 1);
  --cyan-500: rgba(99, 194, 228, 1);
  --cyan-600: rgba(80, 173, 214, 1);
  --cyan-700: rgba(68, 154, 196, 1);
  --cyan-800: rgba(57, 132, 176, 1);
  --cyan-900: rgba(47, 111, 155, 1);
  --cyan-1000: rgba(38, 92, 133, 1);
  --cyan-1100: rgba(28, 73, 112, 1);
  --cyan-1200: rgba(20, 56, 90, 1);
  --cyan-1300: rgba(13, 41, 68, 1);
  --cyan-1400: rgba(7, 29, 49, 1);
  --seafoam-100: rgba(214, 246, 243, 1);
  --seafoam-200: rgba(185, 239, 234, 1);
  --seafoam-300: rgba(162, 231, 225, 1);
  --seafoam-400: rgba(132, 215, 209, 1);
  --seafoam-500: rgba(106, 198, 192, 1);
  --seafoam-600: rgba(82, 178, 173, 1);
  --seafoam-700: rgba(71, 159, 153, 1);
  --seafoam-800: rgba(61, 138, 134, 1);
  --seafoam-900: rgba(51, 117, 113, 1);
  --seafoam-1000: rgba(41, 97, 95, 1);
  --seafoam-1100: rgba(35, 78, 76, 1);
  --seafoam-1200: rgba(30, 59, 58, 1);
  --seafoam-1300: rgba(24, 43, 43, 1);
  --seafoam-1400: rgba(19, 31, 30, 1);
}

.visuallyhidden {
  border: 0;
  clip: rect(0 0 0 0);
  height: auto; /* new - was 1px */
  margin: 0; /* new - was -1px */
  overflow: hidden;
  padding: 0;
  position: absolute;
  width: 1px;
  white-space: nowrap; /* 1 */
}

#masthead ul.primary-links {
  margin: 0;
}

#masthead div[aria-label="Site Logo"] h1 {
  width: max-content;
}

div[aria-label="Site Logo"] {
  height: 100%;
  justify-content: center;
  width: min-content;
}

div[aria-label="Site Logo"],
div.region.region-header,
#block-custom-branding-block div.content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

div#mount-notifications {
  width: 6.526875rem;
  height: 2rem;
  margin-right: auto;
}

div.notification-margin {
  margin-right: 12rem;
}

.row:has(.raft-js-filter-cols) {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-template-rows: 1fr;
  gap: 0.5rem;
}

.chemtracker-module div.raftjs-hovertip,
.chemtracker-module div.raftjs-hovertip div,
.chemtracker-module div.raftjs-hovertip label {
  display: inline-block;
}

.chemtracker-module button.right {
  float: right;
}
.chemtracker-module button.left {
  float: left;
}

:is(#raft-js, #raft-js-app) .d-none {
  display: none;
}

.markup-wrapper {
  max-width: 42rem;
}
</style>
